'use client';

import type { CardLayout, ImageQuality, ImageType, ShowPopover } from '@components/Card2/CardHeader/types';
import { assertIsCardLayout, typeToFieldId } from '@components/Card2/CardHeader/utilities';
import type { CardDetailsFragment } from '@graphql/fragments/cards/core.graphql';
import { useCardImageMutations } from '@hooks/card/image';
import type { Maybe } from 'graphql/jsutils/Maybe';
import { type FC, type ReactNode, createContext, useState } from 'react';

import type { CardImage } from '.cache/__types__';

type CardHeaderEditState = {
  quality?: ImageQuality;
  type?: ImageType;
  image?: Partial<CardImage> | undefined | null;
};

const initialEditState: CardHeaderEditState = {
  image: null,
};

const initialValue = {
  editState: initialEditState,
  showSelectPopover: false,
  showEditPopover: undefined as ShowPopover | undefined,
  /** An image layout to apply to the card header as preview, before the layout has been saved and applied to the card */
  previewImageLayout: undefined as CardLayout | undefined,
  setShowSelectPopover: (show: boolean): void => {
    console.warn('setShowSelectPopover not enabled.');
  },
  setShowEditPopover: (popover: ShowPopover | undefined): void => {
    console.warn('setShowSelectPopover not enabled.');
  },
  setPreviewImageLayout: (layout: CardLayout | undefined): void => {
    console.warn('setPreviewImageLayout not enabled.');
  },
  toggleLock: () => {
    console.warn('toggleLock not enabled.');
  },
  toggleProfileLink: () => {
    console.warn('toggleProfileLink not enabled.');
  },
  setEditImage: ({ image, type, quality }: CardHeaderEditState) => {
    console.warn('setEditState not enabled.');
  },
  saveImage: () => {
    console.warn('saveImage not enabled.');
  },
};

export const CardHeaderEditContext = createContext(initialValue);

export const CardHeaderEditProvider: FC<{
  card?: Maybe<CardDetailsFragment>;
  children?: ReactNode;
}> = ({ card, children }) => {
  const [editState, setEditState] = useState(initialEditState);
  const [showSelectPopover, setShowSelectPopover] = useState(false);
  const [showEditPopover, setShowEditPopover] = useState<ShowPopover | undefined>(undefined);

  const layoutProperty = card?.properties?.layout;
  assertIsCardLayout(layoutProperty?.value);
  const [previewImageLayout, setPreviewImageLayout] = useState<CardLayout | undefined>(
    layoutProperty?.value ?? undefined,
  );

  const { updateOrCreate } = useCardImageMutations();

  const { id, card_id } = editState?.image || {};
  const { type } = editState;

  const saveImage = () => {
    if (!card_id || !type) {
      return;
    }

    return updateOrCreate({ ...editState?.image, card_id }, typeToFieldId(type));
  };

  const setEditImage = ({ image, type, quality }: CardHeaderEditState) => {
    setEditState({ ...editState, image: image, type, quality });
  };

  const toggleLock = () => {
    setEditState({
      ...editState,
      image: { ...editState?.image, is_locked: !editState?.image?.is_locked },
    });
    if (!id) {
      return;
    }
    return saveImage();
  };

  const toggleProfileLink = () => {
    setEditState({
      ...editState,
      image: { ...editState?.image, is_profile_linked: !editState?.image?.is_locked },
    });
    if (!id) {
      return;
    }
    return saveImage();
  };

  const value = {
    editState,
    showSelectPopover,
    showEditPopover,
    previewImageLayout,
    setShowSelectPopover,
    setShowEditPopover,
    setPreviewImageLayout,
    toggleLock,
    toggleProfileLink,
    setEditImage,
    saveImage,
  };

  return <CardHeaderEditContext.Provider value={value}>{children}</CardHeaderEditContext.Provider>;
};
